<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractServiceFees',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: true,
    },
    service: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mapFees = (...args) => {
    return _.chain(args[0])
      .orderBy(['valid_from', 'valid_to'], ['desc', 'desc'])
      .map((item) => item)
      .value();
  };

  const fees = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    fees: fees.value,
    ux: ux.value,
  }));

  const $$load_fees = async () => {
    return mapFees(props.service.fees);
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'fees' }, { name: 'ux' }],
    loaders: { fees: $$load_fees },
    ux: {},
    dataRefs: { fees, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.service,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('service');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_6705964765214521_emit_tcm0g1t9n_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_6705964765214521_close_5ocjbkwmc_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_6705964765214521_showModal_6e2bdng8w_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractServiceEditor',
      props: {
        mode: 'editServiceFee',
        contract: props.contract,
        service: _.cloneDeep(props.service),
        fee: _.cloneDeep($event.scope.fee),
      },
    };
  };
  const act_96700093353179_emit_anouyx3b5_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_96700093353179_close_41yzoaieo_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_96700093353179_showModal_r4891dpr8_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractServiceEditor',
      props: {
        mode: 'addServiceFee',
        contract: props.contract,
        service: _.cloneDeep(props.service),
      },
    };
  };

  const act_6705964765214521_emit_tcm0g1t9n = new actionTypes.EmitAction({
    actionArgs: act_6705964765214521_emit_tcm0g1t9n_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_6705964765214521_close_5ocjbkwmc =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_emit_tcm0g1t9n,
      event: 'close',
      when: inst_evh_6705964765214521_close_5ocjbkwmc_getWhen,
      displayName: 'emitReload',
    });
  const act_6705964765214521_showModal_6e2bdng8w =
    new actionTypes.ShowModalAction({
      actionArgs: act_6705964765214521_showModal_6e2bdng8w_getActionArgs,
      events: [inst_evh_6705964765214521_close_5ocjbkwmc],
    });
  const inst_evh_6705964765214521_clickPreventStop_40adl8h8q =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_showModal_6e2bdng8w,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_6705964765214521_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6705964765214521_clickPreventStop_40adl8h8q],
      event: 'click.prevent.stop',
    });
  const act_96700093353179_emit_anouyx3b5 = new actionTypes.EmitAction({
    actionArgs: act_96700093353179_emit_anouyx3b5_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_96700093353179_close_41yzoaieo =
    new actionTypes.EventHandlerImpl({
      action: act_96700093353179_emit_anouyx3b5,
      event: 'close',
      when: inst_evh_96700093353179_close_41yzoaieo_getWhen,
      displayName: 'emitReload',
    });
  const act_96700093353179_showModal_r4891dpr8 =
    new actionTypes.ShowModalAction({
      actionArgs: act_96700093353179_showModal_r4891dpr8_getActionArgs,
      events: [inst_evh_96700093353179_close_41yzoaieo],
    });
  const inst_evh_96700093353179_clickPreventStop_cfa7byucr =
    new actionTypes.EventHandlerImpl({
      action: act_96700093353179_showModal_r4891dpr8,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_96700093353179_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_96700093353179_clickPreventStop_cfa7byucr],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_6705964765214521_close_5ocjbkwmc,
    emitReload: inst_evh_6705964765214521_close_5ocjbkwmc,
    inst_evh_6705964765214521_clickPreventStop_40adl8h8q,
    inst_evh_6705964765214521_clickPreventStop,
    inst_evh_96700093353179_close_41yzoaieo,
    inst_evh_96700093353179_clickPreventStop_cfa7byucr,
    inst_evh_96700093353179_clickPreventStop,
  };

  const evh_6705964765214521_close_5ocjbkwmc = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_close_5ocjbkwmc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_clickPreventStop_40adl8h8q = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6705964765214521_clickPreventStop_40adl8h8q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_96700093353179_close_41yzoaieo = async (event, scope) => {
    return await actions.inst_evh_96700093353179_close_41yzoaieo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_96700093353179_clickPreventStop_cfa7byucr = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_96700093353179_clickPreventStop_cfa7byucr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_96700093353179_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_96700093353179_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <BListGroup flush>
    <BListGroupItem :key="fee.id" v-for="fee in fees">
      <ItpBox :gap="2">
        <span> • </span>
        <ItpBox>
          <ItpText>
            <ItpText>{{ date(fee.valid_from) }}</ItpText>
            <ItpText v-if="fee.valid_to"> - {{ date(fee.valid_to) }}</ItpText>

            <ItpText class="px-3">
              <span v-if="fee.quantity != null">
                {{ number(fee.quantity) }}
                {{ fee.unit_of_quantity || '' }} &times;
              </span>
              {{ number(fee.amount) }} {{ fee.currency }} [{{ fee.unit }}]
            </ItpText>
          </ItpText>
          <ItpText :text="fee.notes" pre v-if="fee.notes"> </ItpText>
        </ItpBox>
        <ItpButton
          icon="fa-pen"
          :tooltip="gettext('Edit')"
          variant="light"
          size="sm"
          v-if="!readonly"
          @click.prevent.stop="
            evh_6705964765214521_clickPreventStop($event, { fee })
          "
        >
        </ItpButton>
      </ItpBox>
    </BListGroupItem>
  </BListGroup>
  <ItpBox class="ps-4 mt-1">
    <ItpButton
      :text="pgettext('Contract', 'Add service fee...')"
      variant="light"
      size="sm"
      v-if="!readonly"
      @click.prevent.stop="evh_96700093353179_clickPreventStop($event, {})"
    >
    </ItpButton>
  </ItpBox>
</template>
